import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import useLoad from '../utils/useLoad';
import { vpApi, errorMessage, fmtMoney, fmtWan, fmtNum, stageOf, VEHICLE_STATUS, vehicleTitle } from '../api/velopulse';

const FOCUS_BADGE = {
  overdue: { label: '逾期', tone: '' },
  today: { label: '今日', tone: 'blue' },
  done: { label: '完成可勾', tone: 'green' },
};

// 手機 KPI 用的精簡金額（例：286萬）
const fmtWanShort = (n) => (n == null || n === '' ? '—' : `${Math.round(Number(n) / 10000).toLocaleString('en-US')}萬`);

const greeting = () => {
  const h = new Date().getHours();
  if (h < 11) return '早安';
  if (h < 18) return '午安';
  return '晚安';
};

function Workbench() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data, error, loading, reload } = useLoad(() => vpApi.getWorkbench(), []);

  if (loading) return <VpState loading />;
  if (error) {
    return (
      <div className="vp-content">
        <div className="vp-hello"><div><h1>{greeting()}，{user?.name || ''}</h1></div></div>
        <div className="vp-card"><VpState text={errorMessage(error, '工作台資料載入失敗。')} action={{ label: '重新載入', onClick: reload }} /></div>
      </div>
    );
  }

  const { kpi = {}, focus = [], opportunities = [], records = [] } = data || {};

  return (
    <div className="vp-content vp-wb">
      <div className="vp-hello">
        <div>
          <h1><span className="vp-quick-d">{greeting()}，{user?.name || ''}</span><span className="vp-quick-m">工作台</span></h1>
          {data?.headline && <p className="vp-quick-d">{data.headline}</p>}
          {(data?.headline_short || data?.headline) && <p className="vp-quick-m">{data.headline_short || data.headline}</p>}
        </div>
        <div className="vp-actions">
          <button type="button" className="vp-btn" onClick={() => navigate('/records?new=1')}>新增車輛</button>
          <button type="button" className="vp-btn primary" onClick={() => navigate('/opportunities?new=1')}>新增商機</button>
        </div>
      </div>

      <div className="vp-kpi">
        <div className="vp-box"><div className="l"><span className="vp-quick-d">今日</span>待辦</div><div className="v">{fmtNum(kpi.todo_today)}</div></div>
        <div className="vp-box"><div className="l">逾期<span className="vp-quick-d">跟進</span></div><div className={`v${kpi.overdue > 0 ? ' d' : ''}`}>{fmtNum(kpi.overdue)}</div></div>
        <div className="vp-box"><div className="l"><span className="vp-quick-d">本週成交</span>預估</div><div className="v w"><span className="vp-quick-d">{fmtWan(kpi.week_forecast_amount)}</span><span className="vp-quick-m">{fmtWanShort(kpi.week_forecast_amount)}</span></div></div>
      </div>

      <div className="vp-grid">
        <div className="vp-card">
          <h2>今日焦點</h2>
          {!focus.length && <VpState text="今天沒有待辦或逾期的跟進事項。" />}
          {focus.map((f) => {
            const b = FOCUS_BADGE[f.kind] || FOCUS_BADGE.today;
            return (
              <Link className="vp-item" key={f.id} to={f.link || '#'}>
                <span className={`vp-badge ${b.tone}`}>{b.label}</span>
                <div><strong>{f.title}</strong>{f.detail && ` — ${f.detail}`}{f.meta && <div className="vp-sub">{f.meta}</div>}</div>
              </Link>
            );
          })}
        </div>
        <div className="vp-card">
          <h2><span>商機<span className="vp-quick-d">摘要</span></span> <Link to="/opportunities">開啟看板</Link></h2>
          {!opportunities.length && <VpState text="目前沒有進行中的商機。" action={{ label: '新增商機', onClick: () => navigate('/opportunities?new=1') }} />}
          {opportunities.map((o) => {
            const st = stageOf(o.stage);
            return (
              <Link className="vp-item" key={o.id} to={`/opportunities/${o.id}`}>
                <span className={`vp-badge ${st.tone}`}>{st.label}</span>
                <div><strong>{o.title}</strong>{o.amount != null && <span className="vp-wb-inline"> · 預估 {fmtWanShort(o.amount)}</span>}<div className="vp-sub">{[o.amount != null && `預估 ${fmtWan(o.amount)}`, o.summary].filter(Boolean).join(' · ')}</div></div>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="vp-card" style={{ marginTop: 16 }}>
        <h2><span><span className="vp-quick-d">車輛</span>庫存快覽</span> <Link to="/records">開啟紀錄庫</Link></h2>
        {!records.length && <VpState text="目前組織還沒有車輛紀錄。" action={{ label: '新增車輛', onClick: () => navigate('/records?new=1') }} />}
        {records.length > 0 && (
          <>
            <div className="vp-quick-d" style={{ overflowX: 'auto' }}>
              <table className="vp-table">
                <thead><tr><th>編號</th><th>車款</th><th>年式</th><th>里程</th><th>狀態</th><th>標價</th></tr></thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} onClick={() => navigate(`/records/${r.id}`)}>
                      <td>{r.code}</td><td>{vehicleTitle(r)}</td><td>{r.model_year || '—'}</td><td>{fmtNum(r.mileage)}</td>
                      <td>{VEHICLE_STATUS[r.status]?.label || '—'}</td><td className="vp-price">{fmtMoney(r.asking_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="vp-quick-m">
              {records.map((r) => (
                <Link className="vp-item" key={r.id} to={`/records/${r.id}`} style={{ display: 'block' }}>
                  <strong>{r.code}</strong> {vehicleTitle(r)} {r.model_year || ''} · {r.asking_price != null ? `${(r.asking_price / 10000).toFixed(1)} 萬` : '—'}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function WorkbenchPage() {
  return <RequireOrg><Workbench /></RequireOrg>;
}
